import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store';
import { ClientNotification, ShowNotification } from 'app/shared/notifications';
import { ProjectionNotificationsService } from 'app/shared/notifications/projection-notification.service';
import { merge } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MemberCorrespondenceTypeCommandService } from './member-correspondence-type-command.service';
import { MemberCorrespondenceTypeQueryService } from './member-correspondence-type-query.service';
import {
  CancelMemberCorrespondenceTypeChange,
  CancelMemberCorrespondenceTypeChangeFailure,
  CancelMemberCorrespondenceTypeChangeSuccess,
  ChooseMemberCorrespondenceType,
  ChooseMemberCorrespondenceTypeFailure,
  ChooseMemberCorrespondenceTypeSuccess,
  CorrectMemberCorrespondenceTypeEffectiveDate,
  CorrectMemberCorrespondenceTypeEffectiveDateFailure,
  CorrectMemberCorrespondenceTypeEffectiveDateSuccess,
  LoadMemberCorrespondenceTypes,
  LoadMemberCorrespondenceTypesFailure,
  LoadMemberCorrespondenceTypesSuccess
} from './member-correspondence-type.actions';
import { MemberCorrespondenceType } from './member-correspondence-type.model';

export class MemberCorrespondenceTypeStateModel {
  correspondenceTypes: MemberCorrespondenceType[] = [];
  correspondenceTypesLoading = false;
  _memberId: string;
}

@State<MemberCorrespondenceTypeStateModel>({
  name: 'memberCorrespondenceType',
  defaults: new MemberCorrespondenceTypeStateModel(),
})
@Injectable()
export class MemberCorrespondenceTypeState implements NgxsOnInit {
  constructor(
    private queryService: MemberCorrespondenceTypeQueryService,
    private commandService: MemberCorrespondenceTypeCommandService,
    private translate: TranslateService,
    private projectionNotificationsService: ProjectionNotificationsService,
  ) { }

  @Selector()
  static allCorrespondenceTypes(state: MemberCorrespondenceTypeStateModel): MemberCorrespondenceType[] {
    return state.correspondenceTypes || [];
  }

  @Selector()
  static currentCorrespondenceType(state: MemberCorrespondenceTypeStateModel): MemberCorrespondenceType {
    return state.correspondenceTypes.find(correspondenceType => !!correspondenceType.isCurrent);
  }

  @Selector()
  static nextCorrespondenceType(state: MemberCorrespondenceTypeStateModel): MemberCorrespondenceType {
    return state.correspondenceTypes
      .filter(r => r.effectiveOn > new Date())
      .slice()
      .sort((a, b) => a.effectiveOn.getTime() - b.effectiveOn.getTime())?.[0];
  }

  @Selector()
  static correspondenceTypesLoading(state: MemberCorrespondenceTypeStateModel): boolean {
    return state.correspondenceTypesLoading;
  }

  ngxsOnInit({ dispatch, getState }: StateContext<MemberCorrespondenceTypeStateModel>) {
    merge(
      this.projectionNotificationsService.when('CorrespondenceTypeCancelledEvent'),
      this.projectionNotificationsService.when('CorrespondenceTypeAssignedEvent'),
      this.projectionNotificationsService.when('CorrespondenceTypeDateCorrectedEvent'),
    ).subscribe(_ => {
      dispatch(new LoadMemberCorrespondenceTypes(getState()._memberId))
    })
  }

  @Action(LoadMemberCorrespondenceTypes)
  onLoadMemberCorrespondenceTypes(
    { patchState, dispatch }: StateContext<MemberCorrespondenceTypeStateModel>,
    { memberId }: LoadMemberCorrespondenceTypes,
  ) {
    patchState({ correspondenceTypesLoading: true, correspondenceTypes: [], _memberId: memberId });
    return this.queryService.getMemberCorrespondenceTypes(memberId).pipe(
      tap(result => dispatch(new LoadMemberCorrespondenceTypesSuccess(result))),
      catchError(err => dispatch(new LoadMemberCorrespondenceTypesFailure(err))),
    );
  }

  @Action(LoadMemberCorrespondenceTypesSuccess)
  onLoadMemberCorrespondenceTypesSuccess(
    { patchState }: StateContext<MemberCorrespondenceTypeStateModel>,
    { data }: LoadMemberCorrespondenceTypesSuccess,
  ) {
    patchState({ correspondenceTypes: data, correspondenceTypesLoading: false });
  }

  @Action(LoadMemberCorrespondenceTypesFailure)
  onLoadMemberCorrespondenceTypesFailure(
    { patchState }: StateContext<MemberCorrespondenceTypeStateModel>,
    { error }: LoadMemberCorrespondenceTypesFailure,
  ) {
    patchState({ correspondenceTypes: [], correspondenceTypesLoading: false });
  }

  @Action(ChooseMemberCorrespondenceType)
  onChooseMemberCorrespondenceType(
    { patchState, dispatch }: StateContext<MemberCorrespondenceTypeStateModel>,
    { memberId, effectiveOn, isResidenceAssigned }: ChooseMemberCorrespondenceType,
  ) {
    dispatch(
      new ShowNotification(
        ClientNotification.info(this.translate.instant('memberCorrespondenceTypeStore.chooseMemberCorrespondenceType')),
      ),
    );
    patchState({ correspondenceTypesLoading: true });
    return this.commandService.chooseMemberCorrespondenceType(memberId, effectiveOn, isResidenceAssigned).pipe(
      tap(correspondenceTypeId => {
        dispatch(new ChooseMemberCorrespondenceTypeSuccess(correspondenceTypeId, effectiveOn, isResidenceAssigned));
      }),
      catchError(err => dispatch(new ChooseMemberCorrespondenceTypeFailure(err))),
    );
  }

  @Action(CancelMemberCorrespondenceTypeChange)
  onCancelMemberCorrespondenceTypeChange(
    { patchState, dispatch }: StateContext<MemberCorrespondenceTypeStateModel>,
    { memberId, memberCorrespondenceTypeId, effectiveDate }: CancelMemberCorrespondenceTypeChange,
  ) {
    dispatch(
      new ShowNotification(ClientNotification.info(this.translate.instant('memberCorrespondenceTypeStore.cancelMemberCorrespondenceTypeChange'))),
    );
    return this.commandService.cancel(memberId, memberCorrespondenceTypeId, effectiveDate).pipe(
      tap(_ => dispatch(new CancelMemberCorrespondenceTypeChangeSuccess(memberCorrespondenceTypeId))),
      catchError(err => dispatch(new CancelMemberCorrespondenceTypeChangeFailure(err))),
    );
  }

  @Action(CorrectMemberCorrespondenceTypeEffectiveDate)
  onCorrectMemberCorrespondenceTypeEffectiveDate(
    { patchState, dispatch }: StateContext<MemberCorrespondenceTypeStateModel>,
    { memberId, memberCorrespondenceType, newEffectiveDate }: CorrectMemberCorrespondenceTypeEffectiveDate,
  ) {
    dispatch(
      new ShowNotification(ClientNotification.info(this.translate.instant('memberResidenceStore.correctMemberResidenceChange'))),
    );
    return this.commandService.correctEffectiveDate(memberId, memberCorrespondenceType, newEffectiveDate).pipe(
      tap(_ => dispatch(new CorrectMemberCorrespondenceTypeEffectiveDateSuccess(memberCorrespondenceType, newEffectiveDate))),
      catchError(err => dispatch(new CorrectMemberCorrespondenceTypeEffectiveDateFailure(err))),
    );
  }
}
